import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

// Stream identities (design_guidelines.json → stream_visual_identity)
export const STREAMS = {
  assembly: { key: 'assembly', label: 'Assemblies', singular: 'Assembly', base: '/assemblies', icon: 'bg-sky-600', bar: 'bg-sky-500', badge: 'bg-sky-100 text-sky-800', soft: 'bg-sky-50 border-sky-200 text-sky-800' },
  product:  { key: 'product',  label: 'Products',   singular: 'Product',  base: '/products',   icon: 'bg-orange-600', bar: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800', soft: 'bg-orange-50 border-orange-200 text-orange-800' },
  rd:       { key: 'rd',       label: 'R&D',        singular: 'R&D',      base: '/rd',         icon: 'bg-fuchsia-600', bar: 'bg-fuchsia-500', badge: 'bg-fuchsia-100 text-fuchsia-800', soft: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800' },
  neutral:  { key: 'neutral',  label: '',           singular: '',         base: '/',           icon: 'bg-slate-800', bar: 'bg-slate-400', badge: 'bg-slate-100 text-slate-700', soft: 'bg-slate-50 border-slate-200 text-slate-700' },
};

export const KpiCard = ({ label, value, sub, stream = 'neutral', testId }) => (
  <div className="relative bg-white border border-slate-200 p-3.5 rounded-sm" data-testid={testId}>
    <div className={`absolute top-0 left-0 w-full h-1 rounded-t-sm ${STREAMS[stream].bar}`} />
    <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider font-mono">{label}</div>
    <div className="text-xl font-extrabold text-slate-900 mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

/** Consistent page template: breadcrumb → title/icon/badge → description → actions → KPI strip → tabs. */
export const PageHeader = ({ crumbs = [], title, icon: Icon, stream = 'neutral', badge, description, actions, kpis, tabs, testId = 'page-header' }) => {
  const s = STREAMS[stream];
  return (
    <div className="bg-white border border-slate-200 px-6 py-5 rounded-sm" data-testid={testId}>
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="min-w-0">
          {crumbs.length > 0 && (
            <nav className="flex items-center gap-1.5 text-xs font-mono text-slate-500 mb-1" data-testid="page-breadcrumb">
              {crumbs.map((c, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRight className="w-3 h-3" />}
                  {c.to ? <Link to={c.to} className="hover:text-slate-800 hover:underline">{c.label}</Link> : <span>{c.label}</span>}
                </React.Fragment>
              ))}
            </nav>
          )}
          <div className="flex items-center gap-3">
            {Icon && <span className={`p-2 rounded-sm text-white ${s.icon}`}><Icon className="w-5 h-5" /></span>}
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
            {badge && <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-semibold uppercase tracking-wider font-mono ${s.badge}`} data-testid="page-stream-badge">{badge}</span>}
          </div>
          {description && <p className="text-sm text-slate-600 mt-1 max-w-3xl">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap shrink-0" data-testid="page-actions">{actions}</div>}
      </div>
      {kpis && <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4 bg-slate-50/50 p-3 border border-slate-100 rounded-sm" data-testid="page-kpis">{kpis}</div>}
      {tabs && <div className="mt-4 border-t border-slate-100 pt-3">{tabs}</div>}
    </div>
  );
};
